import { FormEvent, useContext, useEffect, useRef, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, TextField } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { CloseOutlined, DeleteOutlined, NewspaperOutlined, PublishOutlined, SaveOutlined } from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { useNavigate, useParams } from 'react-router-dom';
import ApiService from '../../ApiService';
import { ReleaseNote } from '../../types/ReleaseNote';
import { InfoMessageContext } from '../../components/GlobalInfoMessage';
import { ReleaseNotesEditor } from './ReleaseNotesEditor';
import { ReleaseNotesDialog } from './ReleaseNotesDialog';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import AppBreadcrumbs from '../../components/AppBreadcrumbs';
import Block from '../../components/Block';
import CDPageToolbar from '../../components/CDPageToolbar';
import Column from '../../components/Column';
import usePageTitle from '../../hooks/usePageTitle';

export function ReleaseNoteEditorPage() {
    const navigate = useNavigate();
    const { showMessage } = useContext(InfoMessageContext);

    const { id } = useParams();
    const [releaseNote, setReleaseNote] = useState<ReleaseNote>(new ReleaseNote());
    const descriptionRef = useRef<HTMLInputElement>(null);
    const appVersionRef = useRef<HTMLInputElement>(null);
    const [currentVersion, setCurrentVersion] = useState<string>('');
    const [loaded, setLoaded] = useState(false);
    const [content, setContent] = useState<string>('');
    const [preview, setPreview] = useState(false);
    const [isReleaseDisabled, setIsReleaseDisabled] = useState(true);

    const [openConfirm, setOpenConfirm] = useState(false);
    const [openScheduleConfirm, setOpenScheduleConfirm] = useState(false);

    const handleConfirmSchedule = () => {
        if (!releaseNote.releaseDate) {
            showMessage('error', 'Please select a release date before scheduling.');
            return;
        }
        setOpenScheduleConfirm(true);
    };

    const handleCloseScheduleConfirm = () => {
        setOpenScheduleConfirm(false);
    };

    const handleConfirmPublish = () => {
        setOpenConfirm(true);
    };

    const handleCloseConfirm = () => {
        setOpenConfirm(false);
    };

    const handlePublishConfirmed = () => {
        setOpenConfirm(false);
        handlePublish(); // Proceed with publishing
    };

    // build & render title
    usePageTitle({ segments: ['Release Notes', id ? `Edit #${id}` : 'Edit Release Notes'] });

    useEffect(() => {
        let idValue = Number(id);
        if (!isNaN(idValue)) {
            ApiService.getReleaseNote(idValue).then((response) => {
                setReleaseNote(response.data);
                setContent(response.data.content);
                setLoaded(true);
            });
        } else {
            setLoaded(true);
        }
        ApiService.getUiBuildConfig().then((response) => {
            setCurrentVersion(response.data.appVersion);
        });
    }, [id]);

    useEffect(() => {
        checkDisable();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loaded, content]);

    const checkDisable = () => {
        const description = descriptionRef?.current?.value.trim();
        const appVersion = appVersionRef?.current?.value.trim();
        const isContentInValid = content?.trim() === '<p><br></p>' || content?.trim() === '';

        if (description && appVersion && !isContentInValid) {
            setIsReleaseDisabled(false);
        } else {
            setIsReleaseDisabled(true);
        }
    };

    const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        ApiService.updateReleaseNote(getReleaseNote())
            .then((response) => {
                setReleaseNote(response?.data);
                showMessage('success', 'Release Note saved successfully.');
            })
            .catch(() => {});
    };

    const getReleaseNote = () => {
        let description = descriptionRef?.current ? descriptionRef.current.value : '';
        let appVersion = appVersionRef?.current ? appVersionRef.current.value : '';
        return {
            ...releaseNote,
            content: content,
            description: description,
            appVersion: appVersion,
        };
    };

    function handlePreview() {
        setReleaseNote(getReleaseNote());
        setPreview(!preview);
    }

    function handleDelete() {
        if (releaseNote.id) {
            ApiService.deleteReleaseNote(releaseNote.id).then(() => {
                showMessage('success', 'Release Note deleted successfully.');
                setTimeout(() => {
                    navigate(-1);
                }, 500);
            });
        }
    }

    function handlePublish() {
        let releaseNote = getReleaseNote();
        releaseNote.published = true;
        releaseNote.releaseDate = new Date();
        ApiService.updateReleaseNote(releaseNote)
            .then((response) => {
                setReleaseNote(response.data);
                showMessage('success', 'Release Note released successfully.');
            })
            .catch(() => {});
    }

    const updateContent = (newContent: string) => {
        setContent(newContent);
        checkDisable(); // Ensure the release button is enabled/disabled correctly
    };

    const handleScheduleConfirmed = () => {
        setOpenScheduleConfirm(false);
        const releaseDate = releaseNote.releaseDate?.toLocaleDateString();

        let scheduledReleaseNote = getReleaseNote();
        scheduledReleaseNote.releaseDate = releaseNote.releaseDate;
        ApiService.scheduleReleaseNote(scheduledReleaseNote)
            .then(() => {
                showMessage('success', `Release scheduled for ${releaseDate}.`);
            })
            .catch(() => {
                showMessage('error', 'Failed to schedule release.');
            });
    };

    return (
        <Column gap={0} flex={1}>
            <Block>
                <Column gap={1}>
                    <AppBreadcrumbs
                        items={[
                            { label: 'Release Notes', href: '/releaseNotes' },
                            { label: releaseNote.description || '...' },
                        ]}
                    />
                </Column>
            </Block>
            <Block>
                <form onSubmit={handleSubmit}>
                    <div className="editor" style={{ width: '100%' }}>
                        {loaded && (
                            <>
                                <Grid container spacing={1}>
                                    <Grid size={4}>
                                        <TextField
                                            sx={{ paddingBottom: '10px' }}
                                            name="description"
                                            label="Description"
                                            inputRef={descriptionRef}
                                            required={true}
                                            defaultValue={releaseNote.description}
                                            fullWidth={true}
                                            size="small"
                                            variant="filled"
                                            // Check if release button should be disabled whenever description changes
                                            onChange={() => checkDisable()}
                                            slotProps={{
                                                htmlInput: { maxLength: 80 },
                                                inputLabel: { shrink: true },
                                            }}
                                        />
                                    </Grid>
                                    <Grid size={1} />
                                    <Grid size={3}>
                                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                                            <DateTimePicker
                                                label="Release Date"
                                                disablePast={true}
                                                value={releaseNote.releaseDate}
                                                onChange={(newValue) => {
                                                    if (newValue !== null) {
                                                        setReleaseNote((prev) => ({
                                                            ...prev,
                                                            releaseDate: newValue,
                                                        }));
                                                    }
                                                }}
                                                slots={{
                                                    textField: (params) => (
                                                        <TextField variant="filled" size="small" {...params} />
                                                    ),
                                                }}
                                            />
                                        </LocalizationProvider>
                                    </Grid>
                                    <Grid size={2}>
                                        <TextField
                                            sx={{ paddingBottom: '10px' }}
                                            label="App Version"
                                            inputRef={appVersionRef}
                                            required={true}
                                            defaultValue={releaseNote.appVersion}
                                            fullWidth={true}
                                            size="small"
                                            variant="filled"
                                            helperText={'Current Version: ' + currentVersion}
                                            // Check if release button should be disabled whenever app version changes
                                            onChange={() => checkDisable()}
                                            slotProps={{
                                                inputLabel: { shrink: true },
                                            }}
                                        />
                                    </Grid>
                                </Grid>
                                <ReleaseNotesEditor content={content} updateContent={updateContent} />
                            </>
                        )}
                        {preview && <ReleaseNotesDialog onClose={() => setPreview(false)} releaseNote={releaseNote} />}
                    </div>

                    <CDPageToolbar>
                        <Button
                            color="primary"
                            variant="contained"
                            name="save"
                            type="submit"
                            startIcon={<SaveOutlined />}
                        >
                            Save
                        </Button>

                        <Button
                            variant="outlined"
                            color="error"
                            startIcon={<CloseOutlined />}
                            onClick={() => navigate(-1)}
                        >
                            Cancel
                        </Button>

                        <Button
                            variant="outlined"
                            color="error"
                            startIcon={<DeleteOutlined />}
                            disabled={!releaseNote.id}
                            onClick={() => handleDelete()}
                        >
                            Delete
                        </Button>

                        <div style={{ flex: 1 }} />
                        <Button
                            sx={{ marginTop: '5px', marginBottom: '10px' }}
                            color="primary"
                            onClick={handlePreview}
                            variant="outlined"
                            startIcon={<NewspaperOutlined />}
                        >
                            Preview
                        </Button>
                        <Button
                            variant="contained"
                            color="secondary"
                            startIcon={<PublishOutlined />}
                            disabled={isReleaseDisabled || releaseNote?.published}
                            onClick={handleConfirmSchedule}
                        >
                            SCHEDULE
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<PublishOutlined />}
                            disabled={isReleaseDisabled || releaseNote?.published}
                            onClick={handleConfirmPublish}
                        >
                            PUBLISH NOW
                        </Button>
                        <Dialog open={openConfirm} onClose={handleCloseConfirm}>
                            <DialogTitle>Confirm Publish</DialogTitle>
                            <DialogContent>
                                <DialogContentText>Are you sure you want to publish this release?</DialogContentText>
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={handleCloseConfirm} color="primary">
                                    No
                                </Button>
                                <Button onClick={handlePublishConfirmed} color="primary" variant="contained">
                                    Yes
                                </Button>
                            </DialogActions>
                        </Dialog>
                        <Dialog open={openScheduleConfirm} onClose={handleCloseScheduleConfirm}>
                            <DialogTitle>Confirm Schedule</DialogTitle>
                            <DialogContent>
                                <DialogContentText>
                                    Are you sure you want to publish this release on{' '}
                                    {releaseNote.releaseDate?.toLocaleString()}?
                                </DialogContentText>
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={handleCloseScheduleConfirm} color="primary">
                                    No
                                </Button>
                                <Button onClick={handleScheduleConfirmed} color="primary" variant="contained">
                                    Yes
                                </Button>
                            </DialogActions>
                        </Dialog>
                    </CDPageToolbar>
                </form>
            </Block>
        </Column>
    );
}
